import React, { FC, ReactElement } from 'react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import Keycloak, { KeycloakInitOptions } from 'keycloak-js';

import { AuthProvider, AuthProviderProps, useAuth } from 'react-oidc-context';
import { getOidcConfig, OidcUserStorage } from './oidcConfig';
import { Spinner } from '@vwfs-bronson/bronson-react';

function throwIfMissing(value: string | undefined): string {
    if (value) {
        return value;
    } else {
        throw new Error(
            'Missing env variables for authentication. Make sure to specify "REACT_APP_AUTH_PROVIDER_URL", "REACT_APP_AUTH_REALM" und "REACT_APP_AUTH_CLIENT_ID"',
        );
    }
}

const keycloakInstance = () =>
    new Keycloak({
        url: throwIfMissing(process.env.REACT_APP_AUTH_PROVIDER_URL),
        realm: throwIfMissing(process.env.REACT_APP_AUTH_REALM),
        clientId: throwIfMissing(process.env.REACT_APP_AUTH_CLIENT_ID),
    });

export type AuthenticationProviderProps = {
    children?: React.ReactNode;
    loadingComponent?: ReactElement;
    redirectUri?: string;
    initConfig?: KeycloakInitOptions;
};

export const AuthenticationProvider: FC<AuthenticationProviderProps> = ({
    loadingComponent,
    redirectUri,
    initConfig,
    children,
}) => {
    return (
        <ReactKeycloakProvider
            authClient={keycloakInstance()}
            LoadingComponent={loadingComponent}
            initOptions={initConfig || { checkLoginIframe: false, redirectUri }}
        >
            {children}
        </ReactKeycloakProvider>
    );
};

export type AuthenticationOidcProviderProps = {
    userStore?: OidcUserStorage;
    scope?: string;
    redirectUrl?: string;
    postLogoutRedirectUrl?: string;
    children?: React.ReactNode;
    authProviderProps?: AuthProviderProps;
};

const OidcProviderInitializationWrapper: FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isLoading } = useAuth();

    if (isLoading) {
        return <Spinner fullPage />;
    }

    return <>{children}</>;
};

export const AuthenticationOidcProvider: FC<AuthenticationOidcProviderProps> = ({
    scope,
    userStore,
    redirectUrl,
    postLogoutRedirectUrl,
    children,
    authProviderProps = {},
}) => {
    const authUrl = throwIfMissing(process.env.REACT_APP_AUTH_PROVIDER_URL);
    const realm = throwIfMissing(process.env.REACT_APP_AUTH_REALM);
    const clientId = throwIfMissing(process.env.REACT_APP_AUTH_CLIENT_ID);
    const idpHint = throwIfMissing(process.env.REACT_APP_IDP_HINT);

    const oidcConfig = getOidcConfig({
        authUrl,
        realm,
        clientId,
        idpHint,
        redirectUrl,
        postLogoutRedirectUrl,
        scope: scope || 'openid profile email',
        userStore,
    });

    return (
        <AuthProvider {...oidcConfig} {...authProviderProps} key={idpHint}>
            <OidcProviderInitializationWrapper>{children}</OidcProviderInitializationWrapper>
        </AuthProvider>
    );
};
